import { cn } from "@/lib/utils";
import { Gem, Star, Sparkles, Circle } from "lucide-react";

const RARITY_TIERS = {
  "ultra-rare": {
    label: "Ultra Rare",
    color: "text-purple-400",
    bgColor: "bg-purple-500/20",
    borderColor: "border-purple-500/50",
    icon: Gem,
    range: "0-5%",
  },
  "rare": {
    label: "Rare",
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20",
    borderColor: "border-yellow-500/50",
    icon: Star,
    range: "5-20%",
  },
  "uncommon": {
    label: "Uncommon",
    color: "text-blue-400",
    bgColor: "bg-blue-500/20",
    borderColor: "border-blue-500/50",
    icon: Sparkles,
    range: "20-50%",
  },
  "common": {
    label: "Common",
    color: "text-gray-400",
    bgColor: "bg-gray-500/20",
    borderColor: "border-gray-500/50",
    icon: Circle,
    range: "50%+",
  },
};

export function getRarityTier(rarityPercent) {
  // Convert to number if needed
  const num = typeof rarityPercent === 'number' ? rarityPercent : 
              typeof rarityPercent === 'string' ? parseFloat(rarityPercent) : 
              rarityPercent?.percent ? rarityPercent.percent : null;
  
  if (num === null || num === undefined || isNaN(num)) return null;
  if (num <= 5) return "ultra-rare";
  if (num <= 20) return "rare";
  if (num <= 50) return "uncommon";
  return "common";
}

export function getRarityInfo(rarityPercent) {
  const tier = getRarityTier(rarityPercent);
  if (!tier) return null;
  return RARITY_TIERS[tier];
}

export default function RarityBadge({ rarity, size = "sm", showPercent = true, showLabel = false }) {
  // Convert rarity to number and validate
  const rarityNum = typeof rarity === 'number' ? rarity : 
                    typeof rarity === 'string' ? parseFloat(rarity) : 
                    rarity?.percent ? rarity.percent : null;
  
  if (rarityNum === null || rarityNum === undefined || isNaN(rarityNum)) return null;
  
  const tier = getRarityTier(rarityNum);
  if (!tier) return null;
  
  const info = RARITY_TIERS[tier];
  const Icon = info.icon;
  
  const sizeClasses = {
    xs: "text-[10px] px-1 py-0.5 gap-0.5",
    sm: "text-xs px-1.5 py-0.5 gap-1",
    md: "text-sm px-2 py-1 gap-1.5",
    lg: "text-base px-3 py-1.5 gap-2",
  };
  
  const iconSizes = {
    xs: "w-2.5 h-2.5",
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };
  
  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full border font-medium",
        info.bgColor,
        info.borderColor,
        info.color,
        sizeClasses[size]
      )}
      title={`${info.label} (${rarityNum.toFixed(1)}% of players)`}
    >
      <Icon className={iconSizes[size]} />
      {showLabel && <span>{info.label}</span>}
      {showPercent && <span>{rarityNum.toFixed(1)}%</span>}
    </div>
  );
}

export function RarityBar({ rarity, className }) {
  // Convert rarity to number and validate
  const rarityNum = typeof rarity === 'number' ? rarity : 
                    typeof rarity === 'string' ? parseFloat(rarity) : 
                    rarity?.percent ? rarity.percent : null;
  
  if (rarityNum === null || rarityNum === undefined || isNaN(rarityNum)) return null;
  
  const tier = getRarityTier(rarityNum);
  const info = tier ? RARITY_TIERS[tier] : null;
  
  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={info?.color || "text-white/50"}>
          {info?.label || "Unknown"} ({rarityNum.toFixed(1)}%)
        </span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500",
            tier === "ultra-rare" ? "bg-purple-500" :
            tier === "rare" ? "bg-yellow-500" :
            tier === "uncommon" ? "bg-blue-500" :
            "bg-gray-500"
          )}
          style={{ width: `${Math.min(100, rarityNum)}%` }}
        />
      </div>
    </div>
  );
}

export { RARITY_TIERS };
